import { memo } from 'react';
import { ScrollView } from 'react-native';
import { YStack } from 'tamagui';
import { H4 } from 'tamagui';
import { Card } from 'tamagui';
import { XStack } from 'tamagui';
import { View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SizableText } from 'tamagui';
import { Text } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import { Platform } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import { FontAwesome } from '@expo/vector-icons';
import { H6 } from 'tamagui';
// Analytics Tab Component
interface AnalyticsTabProps {
  theme: boolean;
  data: any[];
}

const AnalyticsTab = memo(({ theme, data }: AnalyticsTabProps) => {
  // Calculate analytics from data
  const totalViews = data?.reduce((sum, post) => sum + (post.viewCount || 0), 0) || 0;
  const totalLikes = data?.reduce((sum, post) => sum + (post.likes?.length || 0), 0) || 0;
  const totalComments = data?.reduce((sum, post) => sum + (post.comments?.length || 0), 0) || 0;
  const totalShares = data?.reduce((sum, post) => sum + (post.shares?.length || 0), 0) || 0;
  const totalEngagement = totalLikes + totalComments + totalShares;
  const engagementRate = totalViews > 0 ? ((totalEngagement / totalViews) * 100).toFixed(2) : '0';

  // Top performing posts
  const topPosts = [...(data || [])]
    .sort((a, b) => {
      const aEngagement = (a.likes?.length || 0) + (a.comments?.length || 0) + (a.shares?.length || 0);
      const bEngagement = (b.likes?.length || 0) + (b.comments?.length || 0) + (b.shares?.length || 0);
      return bEngagement - aEngagement;
    })
    .slice(0, 5);

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: 20, gap: 20 }}
      showsVerticalScrollIndicator={false}
    >
      <YStack gap={20}>
        <H4 style={{ color: theme ? "#1e293b" : "#f1f5f9", fontWeight: '700' }}>
          Analytics & Insights
        </H4>

        {/* Key Metrics */}
        <XStack gap={12} flexWrap="wrap" justifyContent="space-between">
          {[
            {
              title: 'Total Views',
              value: totalViews.toLocaleString(),
              icon: 'visibility',
              color: theme ? "#2196f3" : "#64b5f6",
              bgColor: theme ? "#e3f2fd" : "#0d47a1"
            },
            {
              title: 'Total Engagement',
              value: totalEngagement.toLocaleString(),
              icon: 'trending-up',
              color: theme ? "#4caf50" : "#81c784",
              bgColor: theme ? "#e8f5e8" : "#1b5e20"
            },
            {
              title: 'Engagement Rate',
              value: `${engagementRate}%`,
              icon: 'bar-chart',
              color: theme ? "#9c27b0" : "#ce93d8",
              bgColor: theme ? "#f3e5f5" : "#4a148c"
            },
            {
              title: 'Avg Views/Post',
              value: data?.length > 0 ? Math.round(totalViews / data.length).toLocaleString() : '0',
              icon: 'analytics',
              color: theme ? "#ff5722" : "#ff8a65",
              bgColor: theme ? "#fbe9e7" : "#bf360c"
            }
          ].map((stat, index) => (
            <Animatable.View
              key={index}
              animation="fadeInUp"
              delay={index * 100}
              style={{
                flexBasis: Platform.OS === 'web' ? '48%' : '48%',
                backgroundColor: stat.bgColor,
                borderRadius: 16,
                padding: 20,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
              }}
            >
              <MaterialIcons
                name={stat.icon as any}
                size={32}
                color={stat.color}
                style={{ marginBottom: 12 }}
              />
              <Text style={{
                fontSize: 24,
                fontWeight: '800',
                color: stat.color,
                marginBottom: 4
              }}>
                {stat.value}
              </Text>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: theme ? "#64748b" : "#94a3b8",
                textAlign: 'center'
              }}>
                {stat.title}
              </Text>
            </Animatable.View>
          ))}
        </XStack>

        {/* Engagement Breakdown */}
        <Card style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 16,
          padding: 20,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
        }}>
          <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9", marginBottom: 16 }}>
            Engagement Breakdown
          </H6>
          <YStack gap={12}>
            {[
              { label: 'Likes', value: totalLikes, color: theme ? "#e91e63" : "#f48fb1", icon: 'favorite' },
              { label: 'Comments', value: totalComments, color: theme ? "#4caf50" : "#81c784", icon: 'comment' },
              { label: 'Shares', value: totalShares, color: theme ? "#9c27b0" : "#ce93d8", icon: 'share' }
            ].map((item, index) => (
              <XStack key={index} gap={12} alignItems="center" paddingVertical={8}>
                <MaterialIcons name={item.icon as any} size={24} color={item.color} />
                <YStack flex={1}>
                  <SizableText style={{
                    color: theme ? "#1e293b" : "#f1f5f9",
                    fontWeight: '600',
                    fontSize: 14
                  }}>
                    {item.label}
                  </SizableText>
                  <View style={{
                    height: 8,
                    backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                    borderRadius: 4,
                    marginTop: 4,
                    overflow: 'hidden'
                  }}>
                    <View style={{
                      height: '100%',
                      width: `${totalEngagement > 0 ? (item.value / totalEngagement) * 100 : 0}%`,
                      backgroundColor: item.color,
                      borderRadius: 4
                    }} />
                  </View>
                </YStack>
                <SizableText style={{
                  color: item.color,
                  fontWeight: '700',
                  fontSize: 16
                }}>
                  {item.value.toLocaleString()}
                </SizableText>
              </XStack>
            ))}
          </YStack>
        </Card>

        {/* Top Performing Posts */}
        <Card style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 16,
          padding: 20,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
        }}>
          <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9", marginBottom: 16 }}>
            Top Performing Posts
          </H6>
          {topPosts.length > 0 ? (
            <YStack gap={12}>
              {topPosts.map((post, index) => {
                const engagement = (post.likes?.length || 0) + (post.comments?.length || 0) + (post.shares?.length || 0);
                return (
                  <XStack key={post.id || post._id} gap={12} alignItems="center" paddingVertical={8}>
                    <View style={{
                      width: 40,
                      height: 40,
                      borderRadius: 20,
                      backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <SizableText style={{
                        color: theme ? "#1e293b" : "#f1f5f9",
                        fontWeight: '700',
                        fontSize: 16
                      }}>
                        #{index + 1}
                      </SizableText>
                    </View>
                    <YStack flex={1}>
                      <SizableText style={{
                        color: theme ? "#1e293b" : "#f1f5f9",
                        fontWeight: '600',
                        fontSize: 14
                      }}>
                        {post.title || post.content?.substring(0, 40) || 'Untitled Post'}
                      </SizableText>
                      <XStack gap={12} marginTop={4}>
                        <XStack gap={4} alignItems="center">
                          <AntDesign name="like" size={12} color={theme ? "#64748b" : "#94a3b8"} />
                          <SizableText style={{ fontSize: 10, color: theme ? "#64748b" : "#94a3b8" }}>
                            {post.likes?.length || 0}
                          </SizableText>
                        </XStack>
                        <XStack gap={4} alignItems="center">
                          <FontAwesome name="comments" size={12} color={theme ? "#64748b" : "#94a3b8"} />
                          <SizableText style={{ fontSize: 10, color: theme ? "#64748b" : "#94a3b8" }}>
                            {post.comments?.length || 0}
                          </SizableText>
                        </XStack>
                        <XStack gap={4} alignItems="center">
                          <FontAwesome name="share-alt" size={12} color={theme ? "#64748b" : "#94a3b8"} />
                          <SizableText style={{ fontSize: 10, color: theme ? "#64748b" : "#94a3b8" }}>
                            {post.shares?.length || 0}
                          </SizableText>
                        </XStack>
                      </XStack>
                    </YStack>
                    <SizableText style={{
                      color: theme ? "#4caf50" : "#81c784",
                      fontWeight: '700',
                      fontSize: 14
                    }}>
                      {engagement}
                    </SizableText>
                  </XStack>
                );
              })}
            </YStack>
          ) : (
            <SizableText style={{ color: theme ? "#64748b" : "#94a3b8", textAlign: 'center', paddingVertical: 20 }}>
              No posts available
            </SizableText>
          )}
        </Card>
      </YStack>
    </ScrollView>
  );
});
export default AnalyticsTab;